/**
 * Recorrido de demostracion.
 *
 * Se entra desde la pantalla de acceso, sin sesion y sin tocar la base: es la
 * unica pantalla ademas del acceso que un desconocido puede ver, y existe para
 * contestar "¿y esto que hace?" antes de tener cuenta.
 *
 * SE DESLIZA HACIA ABAJO, Y SE IMANTA
 * Los doce capitulos son doce secciones de una sola altura de pantalla dentro de
 * un carril vertical con `scroll-snap`. El dedo baja y la seccion se acomoda
 * sola en su sitio; no hay a medias. Es el gesto que cualquiera ya trae
 * aprendido del telefono, y es lo que convierte doce laminas en un solo
 * documento que se recorre.
 *
 * El imantado lo hace el navegador, no JavaScript. Un carril hecho a mano
 * —escuchando la rueda y animando una transformacion— pierde el arrastre del
 * dedo, la inercia del trackpad y la barra de desplazamiento, y en la PC modesta
 * de una tienda da tirones. `scroll-snap-type: y mandatory` sale gratis y se
 * siente nativo.
 *
 * Y VA GUIADO
 * Cada capitulo termina con la pregunta que el siguiente contesta ("Ya sabes lo
 * que ganas. ¿Y lo que te deben?"). Ese renglon es el que da el razonamiento:
 * quien mira no esta pasando pantallas, esta siguiendo un argumento. Tocarlo
 * tambien baja, para quien prefiere tocar que deslizar.
 *
 * ADEMAS CORRE SOLO
 * Al estilo de las historias: cada capitulo llena su segmento en la barra de
 * arriba y al terminar se desliza al siguiente. Se puede pausar, ir con las
 * flechas o saltar tocando un segmento. Al final se detiene en el cierre, que es
 * donde estan los botones de entrar o repetir; no vuelve a empezar en bucle.
 *
 * LA PAUSA ES DE VERDAD. El temporizador guarda cuanto faltaba y al reanudar
 * retoma desde ahi; el segmento de la barra se pausa con `animation-play-state`,
 * y los capitulos congelan sus escenas. Un boton de pausa que solo detiene la
 * barra pero no la escena seria un adorno.
 *
 * EL AVANCE DE LA BARRA ES CSS, NO ESTADO. Llevarlo en estado de React seria
 * re-dibujar el arbol sesenta veces por segundo durante dos minutos. El segmento
 * activo es una animacion de `transform` cuya duracion la pone el capitulo, y
 * React solo se entera cuando el capitulo termina.
 *
 * Con `prefers-reduced-motion` no arranca solo, no hay imantado animado y cada
 * escena aparece ya terminada.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronDown, ChevronUp, Pause, Play, X } from 'lucide-react';
import { useRouter } from '../../lib/router.js';
import { useBranding } from '../../lib/branding.js';
import { DURATION, EASE } from '../../lib/motion.js';
import { ROUTES } from '../../routes.js';
import { BrandLogo } from '../../components/BrandLogo.js';
import { SeedRain } from '../../components/SeedRain.js';
import { AZIRU, AziruMark } from './aziru.js';
import { CHAPTERS } from './chapters.js';

/**
 * Cuanto se le da al desplazamiento programado para llegar antes de que el
 * carril vuelva a mandar. Un desplazamiento suave de un capitulo tarda entre
 * 300 y 500 ms; el margen evita que el propio movimiento se lea como si el
 * usuario hubiera navegado. Si aun asi no llega —el navegador puede abortar un
 * desplazamiento suave—, al vencer se suelta el mando y gana lo que se ve, que
 * es preferible a quedarse sordo para siempre.
 */
const SETTLE_MS = 900;

export function DemoScreen(): ReactNode {
  const { navigate } = useRouter();
  const branding = useBranding();
  const reduced = Boolean(useReducedMotion());

  const total = CHAPTERS.length;
  const scroller = useRef<HTMLDivElement | null>(null);

  const [index, setIndex] = useState(0);
  // Con movimiento reducido el recorrido no arranca solo.
  const [playing, setPlaying] = useState(
    () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  /** True cuando el ultimo capitulo termino: reproducir vuelve a empezar. */
  const [ended, setEnded] = useState(false);

  // El oyente del desplazamiento vive fuera de React y necesita el indice
  // vigente sin volver a suscribirse en cada cambio.
  const indexRef = useRef(0);
  indexRef.current = index;

  /** Capitulo al que va el desplazamiento que nosotros pedimos, si hay uno. */
  const pending = useRef<number | null>(null);
  /** Momento hasta el cual ese desplazamiento sigue siendo nuestro, no del dedo. */
  const settleUntil = useRef(0);

  const chapter = CHAPTERS[index];

  // ------------------------------- Navegacion -------------------------------

  const scrollToIndex = useCallback(
    (next: number) => {
      const el = scroller.current;
      if (!el) return;

      /*
       * Los saltos de mas de un capitulo van SIN animacion.
       *
       * No es solo gusto: un desplazamiento suave de siete capitulos tarda mas
       * de lo que dura la ventana de gracia, y a mitad del viaje el carril
       * empieza a leer posiciones intermedias como si el usuario hubiera
       * navegado ahi. El recorrido acababa parandose en un capitulo que nadie
       * pidio. Ademas, ver pasar siete laminas borrosas no informa de nada:
       * quien toca el septimo segmento quiere estar en el septimo, ya.
       */
      const far = Math.abs(next - indexRef.current) > 1;

      pending.current = next;
      settleUntil.current = performance.now() + SETTLE_MS;
      el.scrollTo({ top: next * el.clientHeight, behavior: reduced || far ? 'auto' : 'smooth' });
    },
    [reduced],
  );

  const goTo = useCallback(
    (next: number) => {
      if (next < 0 || next >= total) return;
      setEnded(false);
      setIndex(next);
      // El oyente del carril compara contra esta referencia y corre antes del
      // siguiente dibujado; dejarla vieja le haria creer que el movimiento que
      // acabamos de pedir lo hizo el dedo.
      indexRef.current = next;
      scrollToIndex(next);
    },
    [total, scrollToIndex],
  );

  const exit = useCallback(() => navigate(ROUTES.pos), [navigate]);

  const togglePlay = useCallback(() => {
    if (ended) {
      // Reproducir desde el final vuelve a empezar; reanudar un capitulo ya
      // terminado dispararia el temporizador con cero y se sentiria roto.
      setEnded(false);
      setIndex(0);
      scrollToIndex(0);
      setPlaying(true);
      return;
    }
    setPlaying((value) => !value);
  }, [ended, scrollToIndex]);

  // ------------------------- Temporizador del capitulo -------------------------

  // Cuanto le falta al capitulo actual. Se descuenta al pausar y se retoma al
  // reanudar; cambiar de capitulo lo reinicia a la duracion completa, de modo
  // que quien llega deslizando tiene el capitulo entero para leerlo.
  const remaining = useRef(CHAPTERS[0]?.duration ?? 0);

  useEffect(() => {
    remaining.current = CHAPTERS[index]?.duration ?? 0;
  }, [index]);

  useEffect(() => {
    if (!playing) return;

    const startedAt = performance.now();
    const timer = window.setTimeout(() => {
      if (index + 1 < total) {
        goTo(index + 1);
      } else {
        setPlaying(false);
        setEnded(true);
      }
    }, remaining.current);

    return () => {
      window.clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (performance.now() - startedAt));
    };
  }, [playing, index, total, goTo]);

  // --------------------- El carril manda sobre el indice ---------------------

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;

    let frame = 0;

    const read = (): void => {
      frame = 0;
      const height = el.clientHeight;
      if (height === 0) return;

      const seen = Math.round(el.scrollTop / height);

      // Mientras el desplazamiento que pedimos esta en camino, sus propios
      // eventos no cuentan como navegacion del usuario. Se suelta en cuanto
      // llega —sin esperar a que venza el margen— o cuando vence, por si el
      // navegador lo abandono a medias.
      const target = pending.current;
      if (target !== null) {
        if (seen === target || performance.now() >= settleUntil.current) {
          pending.current = null;
        } else {
          return;
        }
      }

      if (seen !== indexRef.current && seen >= 0 && seen < CHAPTERS.length) {
        indexRef.current = seen;
        setEnded(false);
        setIndex(seen);
      }
    };

    // Se lee una vez por cuadro: el desplazamiento dispara decenas de eventos
    // por segundo y cada uno haria un `setState`.
    const onScroll = (): void => {
      if (frame) return;
      frame = requestAnimationFrame(read);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // Al dejar un capitulo, su region interna vuelve arriba. Si alguien lo
  // desplazo explorando y regresa tres capitulos despues, debe recibirlo desde
  // el titular, no a media escena. Se resetean solo los NO activos: tocar el
  // del usuario mientras lo esta leyendo seria arrancarle la pantalla.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    for (let i = 0; i < el.children.length; i++) {
      if (i === index) continue;
      const region = el.children[i]?.querySelector('.overflow-y-auto');
      if (region && region.scrollTop !== 0) region.scrollTop = 0;
    }
  }, [index]);

  // Al cambiar de tamaño la ventana —girar el telefono, abrir el teclado— la
  // altura de las secciones cambia y el carril queda entre dos: se realinea sin
  // animacion, que es lo que espera quien acaba de girar el aparato.
  useEffect(() => {
    const onResize = (): void => {
      const el = scroller.current;
      if (!el) return;
      pending.current = indexRef.current;
      settleUntil.current = performance.now() + SETTLE_MS;
      el.scrollTop = indexRef.current * el.clientHeight;
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // -------------------------------- Teclado --------------------------------

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      // Con el foco sobre un control, la barra espaciadora ya significa
      // "activar ese boton"; sumarle reproducir/pausar haria dos cosas con una
      // sola tecla.
      const target = event.target instanceof HTMLElement ? event.target : null;
      const onControl = Boolean(target?.closest('button, a'));

      if (event.key === 'ArrowDown' || event.key === 'ArrowRight' || event.key === 'PageDown') {
        event.preventDefault();
        goTo(indexRef.current + 1);
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft' || event.key === 'PageUp') {
        event.preventDefault();
        goTo(indexRef.current - 1);
      } else if (event.key === ' ' && !onControl) {
        event.preventDefault();
        togglePlay();
      } else if (event.key === 'Escape') {
        exit();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goTo, togglePlay, exit]);

  if (!chapter) return null;

  return (
    <div className="demo-frame relative h-app overflow-hidden bg-bg text-ink flex flex-col">
      {/*
        El halo del acceso. Va aqui y no dentro del carril para que se quede
        quieto mientras los capitulos pasan por delante: es la luz del cuarto,
        no parte de ninguna lamina. Al ir primero en el documento, todo lo que
        sigue queda encima sin repartir `z-index` por la pantalla.
      */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(60rem 32rem at 50% -12%, color-mix(in srgb, var(--color-primary) 8%, transparent), transparent 70%)',
        }}
      />

      {/* ------------------------------ Encabezado ------------------------------ */}
      <header className="relative shrink-0 pt-safe px-safe-4 sm:px-safe-6">
        {/* Un segmento por capitulo; el activo se llena en tiempo real. */}
        <div className="flex gap-1 pt-3">
          {CHAPTERS.map((entry, i) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`Ir al capitulo ${i + 1}: ${entry.title}`}
              aria-current={i === index ? 'step' : undefined}
              className="flex-1 py-2 -my-2"
            >
              <span className="block h-1 rounded-full bg-line overflow-hidden">
                {i < index || (i === index && ended) ? (
                  <span className="block h-full w-full rounded-full bg-brand-gold" />
                ) : i === index ? (
                  reduced ? (
                    <span className="block h-full w-full rounded-full bg-brand-gold/45" />
                  ) : (
                    <span
                      className="demo-fill block h-full w-full rounded-full bg-brand-gold"
                      style={
                        {
                          '--demo-ms': `${entry.duration}ms`,
                          animationPlayState: playing ? 'running' : 'paused',
                        } as CSSProperties
                      }
                    />
                  )
                ) : null}
              </span>
            </button>
          ))}
        </div>

        {/* Tres columnas y no `justify-between`: con los lados de distinto ancho,
            repartir el espacio dejaria el gafete descentrado. En el telefono la
            fila es mas baja: el navegador ya se comio parte del alto con sus
            propias barras y cada pixel del marco se lo quita a la escena.

            `sm:tall:` y no `sm:`: la fila alta se la gana el ancho pero la paga
            el alto, y un telefono ACOSTADO tiene 844 px de ancho con 390 de
            alto. Pidiendo las dos cosas, ahi se queda con la fila compacta. */}
        <div className="h-12 sm:tall:h-14 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <BrandLogo className="size-8 shrink-0" />
            <span className="hidden sm:block font-semibold tracking-tight truncate">
              {branding.shortName}
            </span>
          </div>

          <span className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 px-3.5 py-1.5 text-[11.5px] sm:text-[12px] font-medium text-brand-gold whitespace-nowrap">
            <span aria-hidden className="size-1.5 rounded-full bg-brand-gold" />
            Demostracion · datos de ejemplo
          </span>

          {/* La firma del estudio: discreta, presente todo el recorrido y con el
              contacto a un toque. En movil solo la marca; el ancho no da para
              la palabra sin apretujar el gafete. */}
          <a
            href={AZIRU.whatsappUrl}
            target="_blank"
            rel="noreferrer"
            title={`${AZIRU.name} · ${AZIRU.tagline}`}
            className="flex items-center justify-end gap-2 text-muted hover:text-ink transition-colors"
          >
            <span className="hidden lg:block text-[10px] uppercase tracking-[0.18em]">por</span>
            <AziruMark className="h-4 w-auto shrink-0" />
            <span className="hidden sm:block font-extrabold tracking-[-0.045em] text-[15px]">
              {AZIRU.name}
            </span>
          </a>
        </div>
      </header>

      {/* -------------------------------- Carril -------------------------------- */}
      {/*
        `overflow-x-hidden` aqui y en la region de cada capitulo no es adorno:
        al poner `overflow-y: auto`, el eje X deja de ser `visible` y cualquier
        desborde de un pixel se vuelve paneo horizontal. En un telefono eso se
        siente como una pagina rota que se mueve de lado; recortar el eje X lo
        hace imposible por construccion.
      */}
      <main
        ref={scroller}
        className="demo-rail relative flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain snap-y snap-mandatory"
        aria-roledescription="recorrido"
      >
        {CHAPTERS.map((entry, i) => {
          const active = i === index;

          return (
            <section
              key={entry.id}
              // `snap-always` impide que un empujon fuerte se salte capitulos:
              // el carril se detiene en cada uno, que es lo que hace que la
              // demostracion se vea completa aunque el dedo venga con prisa.
              className="relative h-full snap-start snap-always flex flex-col"
              aria-label={`${i + 1} de ${total}: ${entry.title}`}
            >
              {/* La lluvia solo donde firma Aziru: en los capitulos con datos,
                  algo cayendo detras de un numero estorba. */}
              {entry.identity ? <SeedRain variant="soft" /> : null}

              {/* Region propia por si un capitulo no cabe a lo alto en un
                  telefono acostado. `overflow-anchor: none` porque las escenas
                  crecen por abajo conforme caen sus beats, y el anclaje de
                  Chrome compensaria ese crecimiento moviendo el titular.

                  `overscroll-contain` es lo que impide que la lamina "baile".
                  Sin el, al llegar al final de esta region el mismo gesto seguia
                  de largo hacia el carril, y el carril tiene imantado
                  obligatorio: el capitulo saltaba al siguiente a media lectura,
                  sin que nadie lo pidiera. Conteniendo el rebote, el dedo que
                  empezo aqui se queda aqui, y para cambiar de capitulo estan las
                  flechas, los segmentos, el boton de abajo o un gesto nuevo. */}
              <div className="relative flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain [overflow-anchor:none] px-safe-4 sm:px-safe-8">
                {/* El relleno generoso lo pide el ancho pero lo paga el alto:
                    en un telefono acostado son 40 px que le faltan a la escena. */}
                <div className="min-h-full flex flex-col justify-center py-2.5 sm:tall:py-5">
                  <entry.Component
                    active={active}
                    playing={playing}
                    onRestart={() => {
                      goTo(0);
                      setPlaying(true);
                    }}
                    onExit={exit}
                  />
                </div>
              </div>

              {/* El puente al siguiente capitulo: la pregunta que deja abierta. */}
              {entry.next ? (
                <motion.button
                  type="button"
                  onClick={() => goTo(i + 1)}
                  // En el telefono el texto y la flecha van en renglon: apilados
                  // cuestan un alto que le hace falta a la escena. Apilarlos
                  // exige ancho Y alto, porque acostado el telefono tiene ancho
                  // de sobra y es justo donde el alto no alcanza.
                  className="shrink-0 mx-auto mb-1 flex flex-row sm:tall:flex-col items-center gap-2 sm:gap-1 px-5 py-1.5 sm:tall:py-2 text-muted hover:text-ink transition-colors"
                  initial={reduced ? false : { opacity: 0 }}
                  animate={{ opacity: active ? 1 : 0 }}
                  transition={{ duration: DURATION.normal, ease: EASE.out, delay: active ? 0.5 : 0 }}
                >
                  <span className="max-w-sm text-center text-[12px] sm:text-[13.5px] leading-snug text-pretty">
                    {entry.next}
                  </span>
                  <ChevronDown className="demo-nudge size-4 text-brand-gold" aria-hidden />
                </motion.button>
              ) : null}
            </section>
          );
        })}
      </main>

      {/* ---------------------------------- Pie ---------------------------------- */}
      <footer className="relative shrink-0 px-safe-4 sm:px-safe-6 pb-safe border-t border-line/60">
        <div className="h-14 sm:tall:h-18 flex items-center gap-2.5 sm:gap-4">
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => goTo(index - 1)}
              disabled={index === 0}
              aria-label="Capitulo anterior"
              className="size-10 grid place-items-center rounded-full border border-line text-muted hover:text-ink hover:border-line-strong transition-colors disabled:opacity-35 disabled:pointer-events-none"
            >
              <ChevronUp className="size-4" />
            </button>

            <button
              type="button"
              onClick={togglePlay}
              aria-label={playing ? 'Pausar el recorrido' : 'Reproducir el recorrido'}
              className="size-11 grid place-items-center rounded-full bg-ink text-bg shadow-card hover:opacity-90 active:scale-95 transition-[opacity,transform]"
            >
              {playing ? (
                <Pause className="size-4 fill-current" strokeWidth={0} />
              ) : (
                <Play className="size-4 fill-current translate-x-[1px]" strokeWidth={0} />
              )}
            </button>

            <button
              type="button"
              onClick={() => goTo(index + 1)}
              disabled={index === total - 1}
              aria-label="Capitulo siguiente"
              className="size-10 grid place-items-center rounded-full border border-line text-muted hover:text-ink hover:border-line-strong transition-colors disabled:opacity-35 disabled:pointer-events-none"
            >
              <ChevronDown className="size-4" />
            </button>
          </div>

          <p className="flex-1 min-w-0 truncate text-[13px] text-muted">
            <span className="tabular font-semibold text-ink">
              {index + 1} de {total}
            </span>{' '}
            · {chapter.title}
          </p>

          <button
            type="button"
            onClick={exit}
            className="shrink-0 inline-flex items-center gap-2 h-10 px-4 sm:px-5 rounded-full border border-line text-[14px] font-medium text-muted hover:text-ink hover:border-line-strong transition-colors"
          >
            <X className="size-4" />
            Salir
          </button>
        </div>
      </footer>
    </div>
  );
}
